import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { FormFillService } from '../../global-services/form-fill.service';
import { FirebaseService } from '../../global-services/firebase.service';
import { FormFillData, SubmitFormResponse } from '../../global-data-models/form-fill.model';
import { Router } from '@angular/router';

export type FormFillState = 'idle' | 'loading' | 'ready' | 'expired' | 'error' | 'submitted';

export interface FormFillUIState {
  state: FormFillState;
  formData: any | null;
  isAuthenticated: boolean;
  assignmentId: string | null;
  accessToken: string | null;
  fillingMethod: string | null;
}

@Injectable({ providedIn: 'root' })
export class FormFillFacade {
  private stateSubject = new BehaviorSubject<FormFillUIState>({
    state: 'idle',
    formData: null,
    isAuthenticated: false,
    assignmentId: null,
    accessToken: null,
    fillingMethod: null,
  });

  state$ = this.stateSubject.asObservable();

  constructor(
    private fb: FormBuilder,
    private loading: GlobalLoadingService,
    private message: GlobalMessageService,
    private formFillService: FormFillService,
    private firebaseService: FirebaseService,
    private router: Router,
  ) {}

  get currentState(): FormFillUIState {
    return this.stateSubject.value;
  }

  private updateState(updates: Partial<FormFillUIState>) {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({ ...currentState, ...updates });
  }

  setMode(isAuthenticated: boolean) {
    this.updateState({ isAuthenticated });
  }

  private get deviceType(): string {
    const userAgent = navigator.userAgent;

    // Detect device type
    let device = '';
    if (/iPhone/i.test(userAgent)) {
      device = 'iphone';
    } else if (/iPad/i.test(userAgent)) {
      device = 'ipad';
    } else if (/Android/i.test(userAgent)) {
      device = 'android';
    } else if (/webOS|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      device = 'mobile';
    } else {
      device = 'desktop';
    }

    // Detect browser
    let browser = '';
    if (/Chrome/i.test(userAgent) && !/Edge/i.test(userAgent)) {
      browser = 'chrome';
    } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
      browser = 'safari';
    } else if (/Firefox/i.test(userAgent)) {
      browser = 'firefox';
    } else if (/Edge/i.test(userAgent)) {
      browser = 'edge';
    } else if (/Opera/i.test(userAgent)) {
      browser = 'opera';
    } else {
      browser = 'unknown';
    }

    return `${device}_${browser}`;
  }

  private formatFillingMethod(baseMethod: string | null): string {
    const device = this.deviceType;
    if (!baseMethod) {
      return device;
    }
    return `${baseMethod}_${device}`;
  }

  async initializeFormFill(assignmentId: string, accessToken: string, fillingMethod?: string | null) {
    try {
      this.loading.show();
      const formattedFillingMethod = this.formatFillingMethod(fillingMethod || null);
      this.updateState({
        state: 'loading',
        assignmentId,
        accessToken,
        fillingMethod: formattedFillingMethod,
      });

      // Check form status first
      const statusResponse = await this.formFillService.checkFormFillStatus(assignmentId, accessToken);

      if (statusResponse.status === 'EXPIRED') {
        this.updateState({ state: 'expired' });
        this.message.showError('Form Expired', 'This form assignment has expired and is no longer available.');
        return;
      }

      // Get form data
      const formResponse = await this.formFillService.getFormForFill(assignmentId, accessToken);

      if (!formResponse.formData) {
        this.updateState({ state: 'error' });
        this.message.showError('Error', 'Failed to load form data.');
        return;
      }

      // Parse form data if it's a string
      let parsedFormData;
      try {
        parsedFormData = typeof formResponse.formData === 'string' ? JSON.parse(formResponse.formData) : formResponse.formData;
      } catch (parseError) {
        this.updateState({ state: 'error' });
        this.message.showError('Error', 'Invalid form data format.');
        return;
      }

      this.updateState({
        state: 'ready',
        formData: parsedFormData,
      });
    } catch (error: any) {
      this.updateState({ state: 'error' });
      const errorMessage = error?.error?.message || error?.message || 'Failed to load form';
      this.message.showError('Error', errorMessage);
    } finally {
      this.loading.hide();
    }
  }

  async submitForm(responseData: any, progress?: number, isComplete: boolean = true) {
    const currentState = this.currentState;

    if (!currentState.assignmentId || !currentState.accessToken) {
      this.message.showError('Error', 'Missing assignment information');
      return;
    }

    try {
      if (isComplete) {
        this.loading.show();
      }

      const submitData: SubmitFormResponse = {
        responseData,
        progress,
        isComplete,
        fillingMethod: currentState.fillingMethod || undefined,
      };

      await this.formFillService.submitForm(currentState.assignmentId, currentState.accessToken, submitData);

      if (!isComplete) {
        return;
      }

      this.updateState({ state: 'submitted' });
      this.message.showSuccess('Success', 'Form submitted successfully!');

      // Navigate away or show success message
      setTimeout(() => {
        this.router.navigate(['/form-submission-success']);
      }, 2000);
    } catch (error: any) {
      const errorMessage = error?.error?.message || error?.message || 'Failed to submit form';
      this.message.showError('Submit Failed', errorMessage);
    } finally {
      this.loading.hide();
    }
  }

  async uploadFile(file: File, assignmentId: string, accessToken: string): Promise<string | null> {
    const currentState = this.currentState;

    if (!currentState.assignmentId) {
      this.message.showError('Upload Error', 'No form assignment available');
      return null;
    }

    try {
      this.loading.show();

      const randomChars = Math.random().toString(36).substring(2, 8); // Generates 6 random characters
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || '';
      const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension from original name
      const uniqueFileName = `${fileName}_${timestamp}_${randomChars}.${fileExtension}`;

      const filePath = await this.firebaseService.uploadPublicFile(file, uniqueFileName);
      await this.formFillService.postUploadFile(currentState.assignmentId, accessToken, filePath);

      this.message.showSuccess('Upload Success', 'File uploaded successfully');
      return filePath; // Return the file path for Survey.js
    } catch (error: any) {
      const errorMessage = error?.error?.message || error?.message || 'Failed to upload file';
      this.message.showError('Upload Failed', errorMessage);
      return null;
    } finally {
      this.loading.hide();
    }
  }

  reset() {
    this.stateSubject.next({
      state: 'idle',
      formData: null,
      isAuthenticated: false,
      assignmentId: null,
      accessToken: null,
      fillingMethod: null,
    });
  }
}
